import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  supabase,
  signInWithGoogle,
  signInWithEmail,
  signOut as supabaseSignOut,
  getUserProfile,
  updateUserProfile,
  getMonthlyUsage,
  getItineraryModificationCount,
  recordItineraryModification,
  exportUserData,
} from '../lib/supabase';
import { isAdminEmail } from '@wander/shared/lib/adminEmails.js';
import { detectModificationType, classifyModificationAction } from '@wander/shared/utils/modificationDetection.js';
import * as Sentry from '@sentry/react';

// ============================================
// TIER LIMITS — imported from @wander/shared (single source of truth)
// ============================================
import { TIER_LIMITS } from '@wander/shared/lib/tiers.js';
export { TIER_LIMITS };

// Suppress informational logs in production (keep console.error for real issues)
const debugLog = process.env.NODE_ENV === 'production' ? () => {} : console.log.bind(console);

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Login modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');

  // Personalization modal state
  const [showPersonalization, setShowPersonalization] = useState(false);

  // Fetch user profile
  const fetchProfile = useCallback(async (userId) => {
    try {
      const profileData = await getUserProfile(userId);
      debugLog('[Auth] Profile loaded from DB:', { tier: profileData?.tier, id: profileData?.id, email: profileData?.email });

      // Auto-upgrade tier to admin if user is in admin emails list (shared source of truth)
      const userEmail = profileData?.email?.toLowerCase();
      const isAdmin = isAdminEmail(userEmail);

      // Referral enforcement: require referred_by unless admin
      if (profileData && !profileData.referred_by && !isAdmin && profileData.tier !== 'admin') {
        debugLog('[Auth] No referral code on profile, blocking access');
        await supabaseSignOut();
        setUser(null);
        setProfile(null);
        setError('A referral code is required to use arrivo. Please sign up with a valid referral code.');
        setLoading(false);
        return null;
      }

      // Tier is managed in Supabase directly — no auto-upgrade
      // isAdminEmail() is only used above for referral gate bypass

      setProfile(profileData);

      // Check if onboarding is needed
      if (profileData && !profileData.onboarding_completed) {
        setShowPersonalization(true);
      }

      return profileData;
    } catch (err) {
      // Ignore AbortError - happens during unmount
      if (err?.name === 'AbortError') return null;
      // Error reported via shared reportError → Sentry
      return null;
    }
  }, []);

  // Fetch usage data
  const fetchUsage = useCallback(async (userId) => {
    try {
      const usageData = await getMonthlyUsage(userId);
      setUsage(usageData);
      return usageData;
    } catch (err) {
      // Ignore AbortError - happens during unmount
      if (err?.name === 'AbortError') return null;
      console.error('Error fetching usage:', err);
      // Set default usage on error so app still works
      const defaultUsage = {
        itineraries_generated: 0,
        destination_recommendations: 0,
        ai_modifications: 0,
        itineraries_limit: 1,                    // Explorer: 1/quarter
        destination_recommendations_limit: 5,    // Explorer: 5/quarter
        modifications_limit: 5,                  // Explorer: 5/itinerary
      };
      setUsage(defaultUsage);
      return defaultUsage;
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;
    let oauthCallbackProcessed = false; // Track if we handled OAuth callback
    let profileFetched = false; // Prevent duplicate fetchProfile calls during SSO
    const abortController = new AbortController();

    const initAuth = async () => {
      try {
        // Handle OAuth callback - check for code in URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        if (code) {
          oauthCallbackProcessed = true;
          // Exchange code for session (OAuth callback)
          debugLog('Processing OAuth callback...');
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('OAuth exchange error:', exchangeError);
            if (mounted) setError(exchangeError.message);
          } else if (data?.session?.user && mounted) {
            debugLog('OAuth successful');
              setUser(data.session.user);
              await fetchProfile(data.session.user.id);
              await fetchUsage(data.session.user.id);
              profileFetched = true; // Mark profile as fetched to prevent duplicate calls
              setShowLoginModal(false);
              setLoading(false);
          }

          // Clean up URL (remove code parameter)
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          // Normal session check — also validate email allowlist
          const { data: { session } } = await supabase.auth.getSession();

          if (mounted && session?.user) {
            setUser(session.user);
            Sentry.setUser({ id: session.user.id, email: session.user.email });
            await fetchProfile(session.user.id);
            await fetchUsage(session.user.id);
            profileFetched = true;
            setLoading(false);
          }
        }
      } catch (err) {
        // Ignore AbortError - happens during rapid unmount/remount (React StrictMode)
        if (err?.name === 'AbortError' || abortController.signal.aborted) {
          // Still set loading=false even on abort so the app doesn't hang
          if (mounted) setLoading(false);
          return;
        }
        console.error('Auth initialization error:', err);
        if (mounted) setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initAuth().catch((err) => {
      // Catch any unhandled rejections from the async init
      if (err?.name !== 'AbortError') {
        console.error('Auth init unhandled error:', err);
      }
    });

    // Safety timeout: never stay on loading screen forever
    const loadingTimeout = setTimeout(() => {
      if (mounted) {
        console.warn('[Auth] Loading safety timeout — forcing loading=false after 10s');
        setLoading(false);
      }
    }, 10000);

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        debugLog('Auth state changed:', event);

        // Ignore events if component unmounted
        if (!mounted) return;

        // Skip SIGNED_IN/INITIAL_SESSION if we already processed OAuth callback
        // This prevents duplicate fetchProfile calls that cause re-renders
        // and break input focus in the PersonalizationModal
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && (oauthCallbackProcessed || profileFetched)) {
          debugLog(`Skipping ${event} - profile already fetched (oauth: ${oauthCallbackProcessed}, fetched: ${profileFetched})`);
          if (event === 'SIGNED_IN') oauthCallbackProcessed = false; // Reset for future sign-ins
          return;
        }

        try {
          if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
            debugLog('Auth event:', event, 'for:', session.user.email);

            setUser(session.user);
            await fetchProfile(session.user.id);
            await fetchUsage(session.user.id);
            profileFetched = true;
            setShowLoginModal(false);
            setLoading(false);
          } else if (event === 'SIGNED_OUT') {
            profileFetched = false; // Reset on sign out so next sign-in works
            setUser(null);
            setProfile(null);
            setUsage(null);
          } else if (event === 'USER_UPDATED' && session?.user) {
            setUser(session.user);
            await fetchProfile(session.user.id);
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            // Just update the user object, no need to refetch profile
            setUser(session.user);
          }
        } catch (err) {
          if (err?.name !== 'AbortError') {
            console.error('Auth state change error:', err);
          }
        }
      }
    );

    return () => {
      mounted = false;
      abortController.abort();
      clearTimeout(loadingTimeout);
      subscription?.unsubscribe();
    };
  }, [fetchProfile, fetchUsage]);

  // Sign in with Google
  const signIn = async () => {
    try {
      setError(null);
      await signInWithGoogle();
      // Redirect happens automatically
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err.message);
      throw err;
    }
  };

  // Sign in with email/password
  // Handles the full flow directly (set user, fetch profile) instead of relying
  // on onAuthStateChange, which can race with initAuth's session check.
  const signInEmail = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const data = await signInWithEmail(email, password);

      if (data?.user) {
        // Email/password users already proved identity — skip allowlist check
        // (allowlist is for OAuth where unknown Google accounts could sign in)
        setUser(data.user);
        await fetchProfile(data.user.id);
        await fetchUsage(data.user.id);
        setShowLoginModal(false);
        setLoading(false);
      }
      return data;
    } catch (err) {
      console.error('Email sign in error:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      await supabaseSignOut();
      // Clear state
      setUser(null);
      setProfile(null);
      setUsage(null);
      // Clear any lingering localStorage items (helps with localhost issues)
      const keysToRemove = Object.keys(localStorage).filter(
        key => key.startsWith('sb-') || key.includes('supabase')
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (err) {
      console.error('Sign out error:', err);
      // Even on error, try to clear local state
      setUser(null);
      setProfile(null);
      setUsage(null);
      setError(err.message);
      throw err;
    }
  };

  // Update profile
  const updateProfile = async (updates) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const updated = await updateUserProfile(user.id, { ...updates, email: user.email });
      setProfile(updated);
      return updated;
    } catch (err) {
      throw err;
    }
  };

  // Complete onboarding
  const completeOnboarding = async (preferences = {}) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const updated = await updateUserProfile(user.id, {
        ...preferences,
        email: user.email,
        onboarding_completed: true,
      });
      setProfile(updated);
      setShowPersonalization(false);
      return updated;
    } catch (err) {
      throw err;
    }
  };

  // Skip onboarding
  const skipOnboarding = async () => {
    if (!user) throw new Error('Not authenticated');

    try {
      const updated = await updateUserProfile(user.id, {
        email: user.email,
        onboarding_completed: true,
      });
      setProfile(updated);
      setShowPersonalization(false);
      return updated;
    } catch (err) {
      throw err;
    }
  };

  // Check usage limit WITHOUT incrementing (for pre-checks before expensive operations)
  // Types: 'itinerary', 'recommendation' (or 'destination_recommendation' alias)
  // Uses TIER_LIMITS as fallback instead of hardcoded values
  const checkUsageLimit = (type) => {
    if (!user || !usage) return { allowed: true }; // Allow if not logged in (guest mode)

    const userTier = getEffectiveTier();
    const tierLimits = TIER_LIMITS[userTier] || TIER_LIMITS.explorer;

    const limits = {
      itinerary: {
        current: usage.itineraries_generated || 0,
        limit: tierLimits.itinerariesPerQuarter,
      },
      recommendation: {
        current: usage.destination_recommendations || 0,
        limit: tierLimits.destinationRecommendations,
      },
      destination_recommendation: {  // alias for backward compat
        current: usage.destination_recommendations || 0,
        limit: tierLimits.destinationRecommendations,
      },
    };

    const check = limits[type];
    if (!check) return { allowed: true };

    if (check.current >= check.limit) {
      return {
        allowed: false,
        message: "You've reached a usage limit. Check your Settings for details.",
        current: check.current,
        limit: check.limit,
      };
    }

    return { allowed: true, current: check.current, limit: check.limit, remaining: check.limit - check.current };
  };

  // Track usage on backend (single source of truth for incrementing)
  // Types: 'itinerary', 'recommendation', 'modification'
  // Call ONCE per logical operation — backend checks limit, increments, returns refreshed usage
  const trackUsageOnBackend = async (type) => {
    if (!user) return { allowed: true }; // Allow if not logged in (guest mode)

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

      const response = await fetch(`${backendUrl}/api/usage/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ type }),
      });

      if (response.status === 429) {
        const data = await response.json();
        return { allowed: false, message: data.message, current: data.current, limit: data.limit };
      }

      if (response.ok) {
        const data = await response.json();
        // Update local usage state from backend response (no separate refresh needed)
        if (data.usage) setUsage(data.usage);
        return { allowed: true };
      }

      return { allowed: true }; // Fail open on unexpected errors
    } catch (err) {
      console.error('trackUsageOnBackend error:', err);
      return { allowed: true }; // Fail open
    }
  };

  // Check modification limit for a specific itinerary (per-itinerary tracking)
  // This is different from trackUsageOnBackend - modifications are per itinerary, not per quarter
  const checkModificationLimit = async (itineraryId, userMessage) => {
    if (!user) return { allowed: true }; // Allow if not logged in

    const tier = getEffectiveTier();
    const tierLimits = TIER_LIMITS[tier] || TIER_LIMITS.explorer;

    // Detect modification type from message
    const modType = detectModificationType(userMessage);
    const modAction = classifyModificationAction(userMessage);
    const isComplex = modType === 'complex';

    // Check if tier allows complex modifications
    if (isComplex && !tierLimits.allowComplexModifications) {
      return {
        allowed: false,
        reason: 'complex_requires_upgrade',
        message: 'This type of change (adding days, extending trips, multi-city replanning) requires arrivo Planner. Upgrade to unlock advanced planning features!',
        upgradePrompt: true,
        modificationType: modType,
        modificationAction: modAction
      };
    }

    // Get current modification count for this itinerary
    // Wrap in try/catch to handle network errors gracefully
    let modData;
    try {
      modData = await getItineraryModificationCount(user.id, itineraryId);
    } catch (error) {
      console.error('Failed to check modification limit:', error);
      // On network error, block the modification to prevent exceeding limits
      return {
        allowed: false,
        reason: 'network_error',
        message: 'Unable to verify your modification limit. Please check your connection and try again.',
        retryable: true,
        modificationType: modType,
        modificationAction: modAction
      };
    }

    const currentCount = modData?.modification_count || 0;
    const limit = tierLimits.modificationsPerItinerary;

    if (currentCount >= limit) {
      return {
        allowed: false,
        reason: 'modification_limit_reached',
        message: tier === 'explorer'
          ? `You've used all ${limit} modifications for this itinerary. Upgrade to Planner for ${TIER_LIMITS.planner.modificationsPerItinerary} modifications per itinerary!`
          : `You've reached ${limit} modifications for this itinerary. Start a new itinerary to continue planning.`,
        upgradePrompt: tier === 'explorer',
        current: currentCount,
        limit: limit,
        modificationType: modType,
        modificationAction: modAction
      };
    }

    return {
      allowed: true,
      remaining: limit - currentCount - 1,
      current: currentCount,
      limit: limit,
      modificationType: modType,
      modificationAction: modAction,
      isComplex
    };
  };

  // Record a modification after it's been applied
  const trackModification = async (itineraryId, userMessage) => {
    if (!user || !itineraryId) return false;

    const modType = detectModificationType(userMessage);
    const modAction = classifyModificationAction(userMessage);
    const isComplex = modType === 'complex';

    return await recordItineraryModification(
      user.id,
      itineraryId,
      modType,
      modAction,
      isComplex
    );
  };

  // Check if user is admin by email
  const isAdminUser = useCallback(() => {
    if (!user?.email) {
      debugLog('[Auth] isAdminUser: No user email');
      return false;
    }
    const email = user.email.toLowerCase();
    const isAdmin = isAdminEmail(email);
    debugLog(`[Auth] isAdminUser: email=${email}, isAdmin=${isAdmin}, dbTier=${profile?.tier}`);
    return isAdmin;
  }, [user?.email, profile?.tier]);

  // Payment past due = user loses premium access until they fix payment
  const isPaymentPastDue = profile?.subscription_status === 'past_due' && !isAdminUser();

  // Effective tier: if payment is past due, downgrade to explorer.
  // IMPORTANT: don't clobber `super-admin` down to `admin` — if the DB
  // explicitly marks a user as super-admin, preserve that (super-admin
  // unlocks the Analytics Dashboard + admin-only UI gates).
  const getEffectiveTier = useCallback(() => {
    const dbTier = profile?.tier;
    if (dbTier === 'super-admin') return 'super-admin';
    if (isAdminUser()) return 'admin';
    if (isPaymentPastDue) return 'explorer';
    return dbTier || 'explorer';
  }, [profile?.tier, profile?.subscription_status, isAdminUser, isPaymentPastDue]);

  // Get tier limits for current user
  const getTierLimits = () => {
    const tier = getEffectiveTier();
    return TIER_LIMITS[tier] || TIER_LIMITS.explorer;
  };

  // Check if user has a specific feature
  const hasFeature = (featureName) => {
    const limits = getTierLimits();
    return limits[featureName] === true;
  };

  // Update analytics consent
  const updateAnalyticsConsent = async (consent) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const updated = await updateUserProfile(user.id, {
        analytics_consent: consent,
        analytics_consent_date: consent ? new Date().toISOString() : null,
      });
      setProfile(updated);
      return updated;
    } catch (err) {
      console.error('Update consent error:', err);
      throw err;
    }
  };

  // Export user data (GDPR)
  const exportData = async (reason) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const data = await exportUserData(user.id, reason);
      return data;
    } catch (err) {
      console.error('Export data error:', err);
      throw err;
    }
  };

  // Open login modal with optional message
  const openLoginModal = (message = '') => {
    setLoginMessage(message);
    setShowLoginModal(true);
  };

  // Close login modal
  const closeLoginModal = () => {
    setShowLoginModal(false);
    setLoginMessage('');
  };

  // Check if user can perform action (tier-based, respects payment status)
  const canPerformAction = (action) => {
    if (!profile) return false;

    const tierPermissions = {
      explorer: ['view_itinerary', 'generate_itinerary', 'modify_itinerary'],
      planner: ['view_itinerary', 'generate_itinerary', 'modify_itinerary', 'save_itinerary', 'export_pdf'],
      admin: ['view_itinerary', 'generate_itinerary', 'modify_itinerary', 'save_itinerary', 'export_pdf', 'admin_panel'],
    };

    const effectiveTier = getEffectiveTier();
    return tierPermissions[effectiveTier]?.includes(action) || false;
  };

  // Get user preferences for AI context.
  // Reads from canonical JSONB (`profile.preferences`) — replaces the legacy
  // snake_case columns that were dropped in the preferences-unification migration.
  const getPreferencesForAI = () => {
    if (!profile) return null;
    const prefs = profile.preferences || {};

    return {
      homeCity: prefs.homeCity,
      travelPace: prefs.pace,                    // canonical: pace
      defaultBudget: prefs.budget,
      travelStyle: prefs.travelStyles,
      interests: prefs.interests || [],
      dietaryRestrictions: prefs.dietary || [],
      accessibilityNeeds: prefs.accessibilityNeeds || [],
      favoriteDestinations: prefs.likedPlaces || [],   // bucketList + likedPlaces both source from here
      leastFavoriteDestinations: prefs.avoidPlaces || [],
      dreamDestinations: prefs.likedPlaces || [],
      lovedPlaces: prefs.likedPlaces || [],
      avoidPlaces: prefs.avoidPlaces || [],
      preferredHotelTier: prefs.hotelTier,
      defaultTripLength: prefs.defaultTripLength,
      currency: prefs.currency || 'USD',
      passportCountry: prefs.passportCountry,
      // Accumulated feedback from past itineraries (for AI learning)
      likedActivities: prefs.likedActivities || [],
      dislikedActivities: prefs.dislikedActivities || [],
      likedCuisines: prefs.likedCuisines || [],
      dislikedCuisines: prefs.dislikedCuisines || [],
      preferredNeighborhoods: prefs.preferredNeighborhoods || [],
      preferredHotelStyles: prefs.preferredHotelStyles || [],
      // Community taste profile + visited places — these are still columns
      tasteProfile: profile.taste_profile || null,
      topDestinations: profile.top_destinations || [],
      visitedCountries: Array.isArray(profile.country) ? profile.country : profile.country ? [profile.country] : [],
    };
  };

  const value = {
    // Auth state
    user,
    profile,
    usage,
    loading,
    error,
    isAuthenticated: !!user,

    // Auth actions
    signIn,
    signInEmail,
    signOut,
    updateProfile,

    // Onboarding
    showPersonalization,
    setShowPersonalization,
    completeOnboarding,
    skipOnboarding,

    // Login modal
    showLoginModal,
    loginMessage,
    openLoginModal,
    closeLoginModal,

    // Usage (quarterly/monthly tracking)
    checkUsageLimit,          // Check WITHOUT incrementing (for pre-checks)
    trackUsageOnBackend,      // Check + increment via backend (single source of truth)
    refreshUsage: () => user && fetchUsage(user.id),
    refreshProfile: () => user && fetchProfile(user.id),

    // Modification tracking (per-itinerary)
    checkModificationLimit,
    trackModification,

    // Tier & Feature access
    getTierLimits,
    hasFeature,
    canPerformAction,
    tier: getEffectiveTier() || 'guest',
    isPaymentPastDue,

    // AI context
    getPreferencesForAI,

    // GDPR
    updateAnalyticsConsent,
    exportData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
